import {
  computed,
  getCurrentInstance,
  inject,
  InjectionKey,
  nextTick,
  onScopeDispose,
  provide,
  ref,
  shallowRef,
  watch,
} from 'vue';
import CatalogV2Service from '@/api/services/catalog-v2.service';
import { useConfirmDialog } from '@/composition/use-confirm-dialog';
import { useMenuStore } from '@/composition/use-menu';
import { useUi } from '@/composition/use-ui';
import {
  categoryFormFromCategory,
  changedCategoryKeys,
  createCategoryPayload,
  diffCategoryForm,
  emptyCategoryForm,
  ICategoryForm,
  ICategoryFormErrors,
  rebaseCategoryAfterSave,
  rebaseCategoryOnRemote,
  TCategoryFormKey,
  validateCategoryForm,
} from '@/domain/catalog/category-form';
import { AlertType } from '@/interfaces/IAlert';
import { ICategory } from '@/interfaces/ICategory';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { useCatalogPage } from './catalog-page-context';
import type { ICatalogEditorTarget } from './catalog-page-state';
import {
  TEditorExtraKey,
  useEditorExtras,
  useEditorPageFlags,
  useSavedFlash,
} from './editor-core';

/** A deep copy (old WebViews lack `structuredClone`). */
function copy<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

/**
 * The category editor (plan §5.15): the same pipeline as the product
 * editor (§5.12) — dirty keys, remote changes, conflict check, the partial
 * PATCH or the create, then translations — for a category. One instance
 * per target (the host keys it).
 */
export function useCategoryEditor(target: ICatalogEditorTarget) {
  const {
    state,
    data,
    access,
    online,
    url,
    actions,
    jumpToCategory,
  } = useCatalogPage();
  const ui = useUi();
  const confirmDialog = useConfirmDialog();
  const menuStore = useMenuStore();
  const restaurantStore = useRestaurantStore();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const isNew = target.id === 'new';
  const categoryId = typeof target.id === 'number' ? target.id : null;
  const restaurantId = data.restaurant.value?.id ?? null;
  /** A new category goes to the menu in view. */
  const menuId = state.menuId;

  let disposed = false;
  function stale(): boolean {
    return disposed || (data.restaurant.value?.id ?? null) !== restaurantId;
  }
  /** Our last saved object: the store echo of our own write is not remote. */
  let ownCategory: ICategory | null = null;

  const category = computed<ICategory | null>(() =>
    categoryId !== null ? data.categoryById.value.get(categoryId) ?? null : null
  );

  const base = shallowRef<ICategoryForm | null>(null);
  const form = ref<ICategoryForm | null>(null);
  const saving = ref(false);
  const { savedFlash, flashSaved } = useSavedFlash();
  const submitted = ref(false);
  const remoteChanged = ref(false);
  /**
   * Keys another device changed while they had unsaved edits here: the
   * rebase keeps the owner's value and «Guardar» asks before overwriting.
   */
  const conflictKeys = ref<TCategoryFormKey[]>([]);
  const failedExtras = ref<TEditorExtraKey[]>([]);
  const extras = useEditorExtras();

  //#region Load

  function resetCreateForm() {
    const stations = restaurantStore.printStations.map(
      (station) => station.stationId
    );
    base.value = emptyCategoryForm(stations);
    form.value = emptyCategoryForm(stations);
    submitted.value = false;
  }

  if (isNew) resetCreateForm();

  watch(
    category,
    (next) => {
      if (!next || isNew) return;
      if (!base.value || !form.value) {
        const loaded = categoryFormFromCategory(next);
        ownCategory = next;
        base.value = loaded;
        form.value = copy(loaded);
        return;
      }
      if (next === ownCategory) return;
      const rebased = rebaseCategoryOnRemote(
        base.value,
        form.value,
        categoryFormFromCategory(next)
      );
      base.value = rebased.base;
      form.value = rebased.form;
      if (rebased.conflicts.length) {
        remoteChanged.value = true;
        conflictKeys.value = [
          ...new Set([...conflictKeys.value, ...rebased.conflicts]),
        ];
      }
    },
    { immediate: true }
  );

  const deleted = computed(
    () =>
      !isNew &&
      !!base.value &&
      !category.value &&
      data.loadStatus.value === 'ready'
  );

  //#endregion

  //#region State

  const dirtyKeys = computed<TCategoryFormKey[]>(() =>
    base.value && form.value ? changedCategoryKeys(base.value, form.value) : []
  );
  const changeCount = computed(
    () => dirtyKeys.value.length + extras.dirtyCount.value
  );
  const dirty = computed(() => changeCount.value > 0);

  const errors = computed<ICategoryFormErrors>(() =>
    form.value ? validateCategoryForm(form.value) : {}
  );
  const visibleErrors = computed<ICategoryFormErrors>(() =>
    submitted.value ? errors.value : {}
  );
  const hasErrors = computed(() => Object.keys(errors.value).length > 0);

  const readOnly = computed(() => !access.value.canManage);
  const canEditAvailability = computed(() => access.value.canToggle);

  useEditorPageFlags(
    state,
    dirty,
    () => form.value?.name ?? category.value?.name ?? ''
  );

  function update<K extends TCategoryFormKey>(key: K, value: ICategoryForm[K]) {
    if (!form.value) return;
    form.value = { ...form.value, [key]: value };
  }

  //#endregion

  //#region Save

  async function confirmConflicts(): Promise<boolean> {
    if (isNew) return true;
    const conflicts = dirtyKeys.value.filter((key) =>
      conflictKeys.value.includes(key)
    );
    if (!conflicts.length) return true;
    const accepted = await confirmDialog.confirm({
      title: t('menuPage.editor.conflictTitleCategory'),
      message: t('menuPage.editor.conflictMessage', {
        fields: conflicts
          .map((key) => t(`menuPage.editor.categoryField.${key}`))
          .join(', '),
      }),
      acceptText: t('menuPage.editor.saveAnyway'),
    });
    if (!accepted && !stale()) remoteChanged.value = true;
    return accepted;
  }

  async function saveCategory(): Promise<ICategory | null | false> {
    if (!form.value || !base.value || restaurantId === null) return false;
    const sent = copy(form.value);
    if (isNew) {
      const result = await CatalogV2Service.createCategory(
        createCategoryPayload(sent, restaurantId, menuId)
      );
      return result.category;
    }
    const patch = diffCategoryForm(base.value, sent);
    if (!patch || categoryId === null) return null;
    const result = await CatalogV2Service.patchCategory(categoryId, patch);
    return result.category;
  }

  /** «Guardar» (or «Crear categoría»); true when everything was saved. */
  async function save(): Promise<boolean> {
    if (!form.value || !base.value || saving.value) return false;
    submitted.value = true;
    if (hasErrors.value || extras.hasInvalid()) return false;
    if (!online.value) {
      ui.showToast(AlertType.Error, t('menuPage.errors.offline'));
      return false;
    }
    if (!(await confirmConflicts()) || stale()) return false;

    const sent = copy(form.value);
    saving.value = true;
    try {
      const saved = await saveCategory();
      // Into the store even if the editor closed meanwhile (the server has
      // it; the store skips another store's rows).
      if (saved) menuStore.upsertCategories([saved], { own: true });
      if (stale() || saved === false) return false;
      if (saved) {
        ownCategory = saved;
        const savedForm = categoryFormFromCategory(saved);
        // A create always ends clean: the editor then opens the category
        // without asking.
        if (isNew) form.value = copy(savedForm);
        else if (form.value)
          form.value = rebaseCategoryAfterSave(form.value, sent, savedForm);
        base.value = savedForm;
      }
      const savedId = saved ? saved.id : categoryId;
      const failed =
        savedId !== null ? await extras.saveExtras(savedId, stale) : [];
      if (stale()) return false;
      failedExtras.value = failed;
      remoteChanged.value = false;
      conflictKeys.value = [];

      if (isNew && saved) {
        actions.trackCategoryCreate();
        ui.showToast(AlertType.Success, t('menuPage.toast.categoryCreated'));
        // The page flags follow `dirty` (a pre-flush watcher) before the
        // route guard reads them.
        await nextTick();
        if (stale()) return false;
        url.replaceTarget('category', saved.id);
        void jumpToCategory(saved.id);
        return !failed.length;
      }
      if (!failed.length) {
        ui.showToast(AlertType.Success, t('menuPage.toast.categorySaved'));
        flashSaved();
      }
      return !failed.length;
    } catch (error) {
      if (!stale()) ui.serverError(error, t('menuPage.errors.save'));
      return false;
    } finally {
      if (!stale()) saving.value = false;
    }
  }

  async function retryExtras(): Promise<boolean> {
    const savedId = category.value?.id ?? null;
    if (savedId === null || saving.value) return false;
    saving.value = true;
    try {
      const failed = await extras.saveExtras(savedId, stale);
      if (stale()) return false;
      failedExtras.value = failed;
      if (!failed.length) {
        ui.showToast(AlertType.Success, t('menuPage.toast.categorySaved'));
        flashSaved();
      }
      return !failed.length;
    } finally {
      if (!stale()) saving.value = false;
    }
  }

  function discard() {
    if (base.value) form.value = copy(base.value);
    extras.resetExtras();
    failedExtras.value = [];
    remoteChanged.value = false;
    conflictKeys.value = [];
    submitted.value = false;
  }

  //#endregion

  onScopeDispose(() => {
    disposed = true;
  });

  return {
    target,
    isNew,
    categoryId,
    category,
    base,
    form,
    saving,
    savedFlash,
    submitted,
    remoteChanged,
    failedExtras,
    deleted,
    dirtyKeys,
    changeCount,
    dirty,
    errors,
    visibleErrors,
    readOnly,
    canEditAvailability,
    update,
    registerExtra: extras.registerExtra,
    save,
    retryExtras,
    discard,
  };
}

export type ICategoryEditor = ReturnType<typeof useCategoryEditor>;

const CATEGORY_EDITOR_KEY: InjectionKey<ICategoryEditor> = Symbol(
  'category-editor'
);

export function provideCategoryEditor(editor: ICategoryEditor): void {
  provide(CATEGORY_EDITOR_KEY, editor);
}

/** Throws outside the category editor. */
export function useCategoryEditorContext(): ICategoryEditor {
  const editor = inject(CATEGORY_EDITOR_KEY, null);
  if (!editor)
    throw new Error('useCategoryEditorContext() needs the category editor');
  return editor;
}
